import { useMemo } from 'react';

import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import {
    getSearchFacets,
    getSelectedOptionFacetValues,
    getSoldFacetsSelectedCreator,
} from '@/redux/modules/searchFacetAggregates.selectors';
import { GlobalState } from '@/redux/store';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FacetValue, ResultFacet } from '@/types/SearchFacet';
import { isArchivedSearch } from '@/utils/queryParams';

// Type definitions for hook parameters and return values
interface SearchUiState {
    deepPageRequiresAuth: boolean;
    error: boolean;
    isInitialLoading: boolean;
    isLoading: boolean;
    isZeroResults: boolean;
    itemIds: number[];
    soldItemIds: number[];
    sort: any[];
    submitted: boolean;
    success: boolean;
    totalBuyNowItems: number;
    totalFound: number;
    totalLiveItems: number;
    totalPages: number;
    totalSoldItems: number;
}

interface OptimizedSearchSelectors {
    archivedSearch: boolean;
    currentFacets: ResultFacet[];
    searchState: SearchUiState;
    selectedCreator: FacetValue | undefined;
}

/**
 * Optimized hook that batches multiple selectors and memoizes results
 * to reduce re-renders and improve performance
 */
export const useOptimizedSearchSelectors = (
    queryParams: Record<string, string | string[] | number | undefined>,
    pathname: string
): OptimizedSearchSelectors => {
    const archivedSearch = useMemo(() => isArchivedSearch(queryParams, pathname), [queryParams, pathname]);

    // Batch selector calls to reduce subscription overhead
    const searchState = useAppSelector(getSearchUiState);
    const currentFacets = useAppSelector(getSearchFacets);
    const soldSelectedCreator = useAppSelector(getSoldFacetsSelectedCreator);

    const upcomingSelectedCreator = useAppSelector((state: GlobalState) =>
        getSelectedOptionFacetValues(state, FacetTypes.CREATOR_ID)
    );

    // Memoize derived values to prevent unnecessary recalculations
    const memoizedValues = useMemo(() => {
        const selectedCreator =
            !searchState.totalFound && !archivedSearch ? soldSelectedCreator : upcomingSelectedCreator;

        return {
            archivedSearch,
            currentFacets,
            searchState,
            selectedCreator,
        };
    }, [archivedSearch, currentFacets, searchState, soldSelectedCreator, upcomingSelectedCreator]);

    return memoizedValues;
};
